import React, {
  ComponentType,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import HappyHourContext, { HappyHourContextValue } from "./HappyHourContext";

/**
 * TBT-FIX: monte le vrai `HappyHourProvider` hors du chunk app.js eager.
 *
 * Le chunk est injecté par le loader web-performance après `app.js` ;
 * ce provider ne retarde plus le chargement via `runAfterLcp`.
 *
 * REMOUNT-FIX: `{children}` reste sous un Context.Provider stable ; le vrai
 * provider monte en sibling. Évite Fragment→Provider qui remountait tout
 * l’arbre (blink pleine page + reload images).
 */
type ProviderComponent = ComponentType<{ children: ReactNode }>;

const inertValue: HappyHourContextValue = {
  refresh: () => {},
};

const HappyHourDeferredProvider = ({ children }: { children: ReactNode }) => {
  const [Real, setReal] = useState<ProviderComponent | null>(null);
  const [value, setValue] = useState<HappyHourContextValue>(inertValue);

  useEffect(() => {
    if (Real) return;
    let cancelled = false;
    void import(
      /* webpackChunkName: "happy-hour-provider" */ "./HappyHourProvider"
    ).then((mod) => {
      if (cancelled) return;
      setReal(() => mod.HappyHourProvider);
    });
    return () => {
      cancelled = true;
    };
  }, [Real]);

  return (
    <>
      <HappyHourContext.Provider value={value}>
        {children}
      </HappyHourContext.Provider>
      {Real ? (
        <Real>
          <ContextValueMirror onValue={setValue} />
        </Real>
      ) : null}
    </>
  );
};

function ContextValueMirror({
  onValue,
}: {
  onValue: (value: HappyHourContextValue) => void;
}) {
  const ctx = useContext(HappyHourContext);
  const prev = useRef<HappyHourContextValue | null>(null);
  useEffect(() => {
    const p = prev.current;
    if (
      p &&
      p.data === ctx.data &&
      p.loading === ctx.loading &&
      p.refresh === ctx.refresh &&
      p.topBannerClosed === ctx.topBannerClosed &&
      p.setTopBannerClosed === ctx.setTopBannerClosed
    ) {
      return;
    }
    prev.current = ctx;
    onValue(ctx);
  }, [ctx, onValue]);
  return null;
}

export default HappyHourDeferredProvider;
